import React from 'react';
import { FileText } from 'lucide-react';
import './ProjectDocumentation.css';

// --- COMPREHENSIVE SDLC CONTENT (PRODUCTION GRADE) ---
const DOC_CONTENT_HTML = `
  <div class="doc-window-container">
    <div class="doc-sidebar">
      <div class="doc-sidebar-header">
        <h3>SYSTEM_LOG</h3>
        <span class="version-badge">REL_1.0.4</span>
      </div>
      <nav>
        <div class="nav-group">
          <span class="nav-label">PHASE I: STRATEGY</span>
          <a href="#vision" class="doc-link">1.0 Vision & Persona</a>
          <a href="#design-system" class="doc-link">1.1 Design System</a>
        </div>
        <div class="nav-group">
          <span class="nav-label">PHASE II: ARCHITECTURE</span>
          <a href="#frontend-arch" class="doc-link">2.0 Frontend Stack</a>
          <a href="#routing-logic" class="doc-link">2.1 Smart Routing</a>
        </div>
        <div class="nav-group">
          <span class="nav-label">PHASE III: ENGINEERING</span>
          <a href="#webgl-core" class="doc-link">3.0 WebGL Engine</a>
          <a href="#preloader-logic" class="doc-link">3.1 Boot Sequence</a>
          <a href="#performance" class="doc-link">3.2 Performance</a>
        </div>
        <div class="nav-group">
          <span class="nav-label">PHASE IV: QA & RELEASE</span>
          <a href="#a11y" class="doc-link">4.0 Accessibility</a>
          <a href="#future-roadmap" class="doc-link">4.1 Roadmap</a>
        </div>
      </nav>
      <div class="sidebar-footer">
        <p>CONFIDENTIAL<br/>Auth: A. Bhowal</p>
      </div>
    </div>
    
    <main class="doc-main">
      <header class="main-header">
        <div class="header-pre">DOC_ID: ARCH_2026_V1</div>
        <h1>System Architecture & Engineering Decisions</h1>
        <p class="lead">A technical deep-dive into the design, constraints, and engineering implementation of the portfolio platform.</p>
        
        <div class="meta-grid">
           <div class="meta-item">
             <label>STACK</label>
             <span>React 18 / Three.js</span>
           </div>
           <div class="meta-item">
             <label>RENDER</label>
             <span>Client-Side (CSR)</span>
           </div>
           <div class="meta-item">
             <label>THEME</label>
             <span>Dual-State CSS Var</span>
           </div>
           <div class="meta-item">
             <label>STATUS</label>
             <span style="color:var(--accent)">PRODUCTION</span>
           </div>
        </div>
      </header>

      <section id="vision" class="doc-section">
        <h2>1.0 Vision & Persona Definition</h2>
        <p>
          <strong>The Problem:</strong> Traditional developer portfolios (PDF resumes, static HTML) fail to capture the <em>dynamic</em> and <em>adversarial</em> nature of Cybersecurity engineering. They list skills ("Threat Modeling", "SOC Ops") without providing a medium that demonstrates the mindset required to execute them.
        </p>
        <p>
          <strong>The Solution:</strong> The "Command & Control" (C2) Paradigm.
          We architected the platform not as a brochure, but as a functional dashboard. The interface mimics the tools a security engineer uses daily (Kali Linux, SIEM Dashboards, IDEs).
        </p>
        <div class="callout">
          <strong>Design Philosophy:</strong> "Diegetic UI". The user interface exists <em>within</em> the world of the persona. The Preloader isn't just a loading screen; it is a kernel boot sequence. The navigation isn't a navbar; it is a Heads-Up Display (HUD).
        </div>
      </section>

      <div class="divider"></div>

      <section id="design-system" class="doc-section">
        <h2>1.1 Design System: "Red Team / Blue Team"</h2>
        <p>
          Cybersecurity is defined by the conflict between Offense (Red) and Defense (Blue). 
          We rejected the standard "Dark/Light" mode dichotomy in favor of a thematic shift that alters the user's role.
        </p>
        
        <div class="grid-2">
          <div class="card">
            <h4>MODE A: RED TEAM (Dark)</h4>
            <p><strong>Archetype:</strong> The Attacker / The Terminal.</p>
            <p><strong>Color Psychology:</strong></p>
            <ul>
              <li><strong>Background:</strong> <code>#0d1117</code> (GitHub Dark Dimmed). Reduces eye strain during long coding sessions.</li>
              <li><strong>Primary Accent:</strong> <code>#00FF41</code> (Phosphor Green). Historically associated with monochrome CRT monitors.</li>
              <li><strong>Secondary Accent:</strong> <code>#00F0FF</code> (Cyberpunk Cyan). Used for data visualization to cut through the dark UI.</li>
            </ul>
          </div>
          <div class="card">
            <h4>MODE B: BLUE TEAM (Light)</h4>
            <p><strong>Archetype:</strong> The Analyst / The Clean Room.</p>
            <p><strong>Color Psychology:</strong></p>
            <ul>
              <li><strong>Background:</strong> <code>#FFFFFF</code> (Sterile White). Represents compliance, documentation, and corporate governance.</li>
              <li><strong>Primary Accent:</strong> <code>#0055aa</code> (Deep Azure). Projects stability and trust.</li>
              <li><strong>Contrast Fix:</strong> The Neon Cyan used in dark mode failed WCAG contrast tests on white. We implemented a CSS variable override to shift it to Deep Cerulean (<code>#0077cc</code>) automatically.</li>
            </ul>
          </div>
        </div>
      </section>

      <div class="divider"></div>

      <section id="frontend-arch" class="doc-section">
        <h2>2.0 Frontend Architecture</h2>
        
        <h3>2.0.1 Component Composition</h3>
        <p>
          The application follows an <strong>Atomic Design</strong> hybrid approach.
        </p>
        <ul>
          <li><strong>Atoms:</strong> Buttons (<code>.cyber-btn</code>), Tags (<code>.tech-tag</code>), Icons (Lucide React).</li>
          <li><strong>Molecules:</strong> Cards (<code>ProjectCard</code>), List Items (<code>SkillItem</code>).</li>
          <li><strong>Organisms:</strong> Sections (<code>Hero</code>, <code>SOCDashboard</code>, <code>SkillsMatrix</code>).</li>
          <li><strong>Templates:</strong> Pages (<code>Portfolio.jsx</code>, <code>ArticleDetail.jsx</code>).</li>
        </ul>

        <h3>2.0.2 The "Headless" Data Pattern</h3>
        <p>
          To ensure the codebase remains maintainable, all content is decoupled from the presentation layer. 
          A centralized <code>mockData.js</code> file acts as a local CMS.
        </p>
        <div class="code-block">
          <div class="code-label">Architecture Pattern: Data Abstraction</div>
          <pre>
// src/mockData.js
export const mockData = {
  hero: { headline: "...", cta: "..." },
  skills: [ { category: "AppSec", items: [...] } ],
  projects: [...]
};

// src/components/Hero.jsx
const Hero = ({ data }) => { 
  // Component is purely presentational
  return &lt;h1&gt;{data.headline}&lt;/h1&gt;; 
};</pre>
        </div>
      </section>

      <div class="divider"></div>

      <section id="routing-logic" class="doc-section">
        <h2>2.1 Smart Routing Middleware</h2>
        <p><strong>ADR-004: Hybrid Navigation Strategy</strong></p>
        <p>
          The application uses a "One-Page Scroll" architecture for the main portfolio but requires separate routes for SEO-friendly Articles.
          <strong>The Conflict:</strong> Standard anchor links (<code>#skills</code>) break when clicked from a sub-route (<code>/article/1</code>).
        </p>
        <p><strong>The Solution:</strong> We engineered a custom routing middleware in <code>Header.jsx</code>.</p>
        <div class="code-block">
          <pre>
const handleNavClick = (id) => {
  if (location.pathname === '/') {
    // Case A: Internal Scroll
    // Uses native DOM API for performance
    document.getElementById(id).scrollIntoView();
  } else {
    // Case B: Cross-Route Navigation
    // 1. Push to history stack
    // 2. Pass 'targetId' in location state
    navigate('/', { state: { scrollToId: id } });
  }
};</pre>
        </div>
      </section>

      <div class="divider"></div>

      <section id="webgl-core" class="doc-section">
        <h2>3.0 WebGL Engineering (Three.js)</h2>
        <h3>The Digital Core</h3>
        <p>
          The Hero section features a custom particle system rendered via Three.js. 
          To ensure 60FPS performance on non-gaming laptops, we applied aggressive optimization techniques.
        </p>

        <h3>3.0.1 Geometry Instancing</h3>
        <p>
          <strong>Naive Approach:</strong> Creating 1,000 <code>THREE.Mesh</code> objects for stars. (Cost: 1,000 Draw Calls).<br/>
          <strong>Optimized Approach:</strong> Using <code>THREE.BufferGeometry</code> with a Float32Array for positions. (Cost: 1 Draw Call).
        </p>

        <h3>3.0.2 Mobile "Kill Switch"</h3>
        <p>
          WebGL contexts are battery drains. We implemented a render guard that checks the viewport width before initialization.
        </p>
        <div class="code-block">
          <pre>
// src/components/HeroGlobe.jsx
const isMobile = window.innerWidth < 768;

if (isMobile) {
  // Return null immediately. 
  // Do not instantiate WebGLRenderer.
  return null; 
}</pre>
        </div>
      </section>

      <div class="divider"></div>

      <section id="preloader-logic" class="doc-section">
        <h2>3.1 The Boot Sequence</h2>
        <p>
          The <code>Preloader.jsx</code> component is not a video. It is a procedural animation engine designed to mask the React hydration phase.
        </p>
        <ul>
          <li><strong>Variable Typing Speed:</strong> Uses randomized delay (5-20ms) between lines to simulate CPU processing variance.</li>
          <li><strong>DOM Ref Scrolling:</strong> A <code>useRef</code> hook automatically pushes the scroll container to the bottom as new logs are appended.</li>
        </ul>
      </section>

      <div class="divider"></div>

      <section id="performance" class="doc-section">
        <h2>3.2 Performance & Metrics</h2>
        <div class="tech-table">
          <div class="table-row">
            <span>First Contentful Paint</span>
            <span>0.8s</span>
            <span>Preloader renders immediately via critical CSS.</span>
          </div>
          <div class="table-row">
            <span>Bundle Size</span>
            <span>~240kb (Gzipped)</span>
            <span>Code splitting applied to Three.js chunks.</span>
          </div>
        </div>
      </section>

      <div class="divider"></div>

      <section id="future-roadmap" class="doc-section">
        <h2>4.1 Roadmap & Debt</h2>
        <p>
          <strong>Technical Debt:</strong> The current CSS architecture relies on global variables. Migration to CSS Modules would improve scoping safety.
        </p>
        <p>
          <strong>Future Feature:</strong> "Live Threat Map". Connecting the Hero Globe to a real-time websocket to visualize live attacks.
        </p>
      </section>
      
      <footer class="doc-footer">
        <p>SYSTEM STATUS: <span style="color:var(--accent)">OPTIMAL</span> // END OF STREAM</p>
      </footer>
    </main>
  </div>
`;

const ProjectDocumentation = () => {
  
  const openDocumentationWindow = () => {
    const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';

    const width = 1200;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // FIX: Removed invalid backslashes from template string
    const win = window.open(
      "", 
      "ProjectDocs", 
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
    );

    if (!win) {
      alert("Please allow popups to view the documentation window.");
      return;
    }

    // FIX: Removed invalid backslashes from template string
    const styles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Inter:wght@400;600&display=swap');
        
        :root {
          /* Default Dark Mode Variables */
          --bg-core: #0d1117;
          --bg-panel: #161b22;
          --text-main: #c9d1d9;
          --text-muted: #8b949e;
          --accent: #00FF41;
          --border: #30363d;
          --code-bg: #000;
          --nav-hover: rgba(255, 255, 255, 0.05);
        }

        /* Light Mode Overrides (injected based on parent state) */
        [data-theme="light"] {
          --bg-core: #ffffff;
          --bg-panel: #f6f8fa;
          --text-main: #24292f;
          --text-muted: #57606a;
          --accent: #0066cc;
          --border: #d0d7de;
          --code-bg: #24292f;
          --nav-hover: rgba(0, 0, 0, 0.05);
        }
        
        body { margin: 0; padding: 0; background: var(--bg-core); color: var(--text-main); font-family: 'Inter', sans-serif; overflow: hidden; }
        
        /* LAYOUT */
        .doc-window-container { display: flex; height: 100vh; }
        
        /* SIDEBAR */
        .doc-sidebar { width: 280px; background: var(--bg-panel); border-right: 1px solid var(--border); display: flex; flex-direction: column; flex-shrink: 0; }
        .doc-sidebar-header { padding: 30px; border-bottom: 1px solid var(--border); }
        .doc-sidebar-header h3 { margin: 0; color: var(--accent); font-family: 'Space Mono', monospace; font-size: 16px; letter-spacing: -1px; }
        .version-badge { font-size: 10px; opacity: 0.6; font-family: 'Space Mono', monospace; background: rgba(125,125,125,0.1); padding: 2px 6px; border-radius: 4px; margin-top: 5px; display: inline-block; }
        
        nav { padding: 20px 0; display: flex; flex-direction: column; flex: 1; overflow-y: auto; }
        .nav-group { margin-bottom: 20px; }
        .nav-label { font-family: 'Space Mono', monospace; font-size: 10px; color: var(--text-muted); opacity: 0.5; padding: 0 30px; margin-bottom: 8px; display: block; font-weight: 700; }
        
        .doc-link { padding: 8px 30px; color: var(--text-muted); text-decoration: none; font-size: 13px; border-left: 2px solid transparent; transition: 0.2s; display: block; }
        .doc-link:hover { background: var(--nav-hover); color: var(--text-main); border-left-color: var(--accent); }
        
        .sidebar-footer { padding: 20px; border-top: 1px solid var(--border); font-size: 10px; color: var(--text-muted); font-family: 'Space Mono', monospace; opacity: 0.5; }

        /* MAIN CONTENT */
        .doc-main { flex: 1; padding: 0; overflow-y: auto; scroll-behavior: smooth; position: relative; }
        
        .main-header { padding: 60px 80px 40px; border-bottom: 1px solid var(--border); margin-bottom: 40px; background: var(--bg-panel); }
        .header-pre { font-family: 'Space Mono', monospace; font-size: 11px; color: var(--accent); margin-bottom: 10px; opacity: 0.8; }
        .main-header h1 { font-family: 'Space Mono', monospace; font-size: 28px; margin: 0 0 10px 0; color: var(--text-main); line-height: 1.3; }
        .lead { font-size: 16px; color: var(--text-muted); margin: 0 0 30px 0; font-weight: 400; max-width: 700px; line-height: 1.6; }
        
        .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; border-top: 1px solid var(--border); padding-top: 20px; }
        .meta-item { display: flex; flex-direction: column; gap: 5px; }
        .meta-item label { font-family: 'Space Mono', monospace; font-size: 10px; color: var(--text-muted); opacity: 0.6; }
        .meta-item span { font-size: 12px; font-weight: 600; color: var(--text-main); }

        .doc-section { padding: 0 80px; margin-bottom: 80px; max-width: 900px; }
        
        h2 { font-family: 'Space Mono', monospace; font-size: 22px; color: var(--accent); margin-top: 0; border-bottom: 1px solid var(--border); padding-bottom: 15px; margin-bottom: 25px; }
        h3 { font-size: 16px; margin-top: 40px; margin-bottom: 15px; color: var(--text-main); font-weight: 700; }
        p { font-size: 15px; line-height: 1.7; color: var(--text-muted); margin-bottom: 15px; }
        ul { padding-left: 20px; color: var(--text-muted); margin-bottom: 20px; }
        li { margin-bottom: 10px; line-height: 1.6; font-size: 14px; }
        strong { color: var(--text-main); }
        
        .divider { height: 1px; background: var(--border); margin: 60px 80px; opacity: 0.5; }
        
        /* COMPONENTS */
        .callout { background: rgba(0, 255, 65, 0.05); border-left: 3px solid var(--accent); padding: 20px; margin: 30px 0; font-size: 14px; color: var(--text-main); }
        [data-theme="light"] .callout { background: rgba(0, 100, 255, 0.05); }

        .code-block { background: var(--code-bg); padding: 20px; border-radius: 6px; border: 1px solid var(--border); margin: 30px 0; overflow-x: auto; }
        .code-label { font-family: 'Space Mono', monospace; font-size: 10px; color: #8b949e; border-bottom: 1px solid #30363d; padding-bottom: 8px; margin-bottom: 10px; display: block; }
        pre { margin: 0; font-family: 'Consolas', monospace; color: #a5d6ff; font-size: 13px; line-height: 1.5; }
        
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 30px; }
        .card { background: var(--bg-panel); padding: 25px; border: 1px solid var(--border); border-radius: 6px; }
        .card h4 { margin: 0 0 10px 0; color: var(--accent); font-family: 'Space Mono', monospace; font-size: 12px; }
        .card p { margin: 0 0 10px 0; font-size: 13px; }
        
        .tech-table { display: grid; gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 6px; overflow: hidden; margin-top: 20px; }
        .table-row { display: grid; grid-template-columns: 180px 150px 1fr; background: var(--bg-panel); }
        .table-row span { padding: 12px 20px; font-size: 13px; color: var(--text-muted); border-right: 1px solid var(--border); }
        .table-row span:last-child { border-right: none; }
        .table-row span:first-child { color: var(--text-main); font-weight: 600; }

        .doc-footer { text-align: center; padding: 60px; font-family: 'Space Mono', monospace; font-size: 11px; color: var(--text-muted); opacity: 0.6; border-top: 1px solid var(--border); margin-top: 60px; }
        
        /* SCROLLBAR */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: var(--bg-core); }
        ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: var(--accent); }
      </style>
    `;

    // 5. Write to window
    win.document.write(`
      <!DOCTYPE html>
      <html data-theme="${currentTheme}">
        <head>
          <title>System Architecture // Engineering Logs</title>
          ${styles}
        </head>
        <body>
          ${DOC_CONTENT_HTML}
        </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <button 
      className="doc-floating-trigger" 
      onClick={openDocumentationWindow}
      title="View System Architecture Documentation"
    >
      <FileText size={20} />
      <span className="doc-trigger-text">SDLC_LOGS</span>
    </button>
  );
};

export default ProjectDocumentation;